import { useState, useCallback, useEffect } from 'react';

const WINNING_COMBINATIONS = [
  [0, 1, 2], // Top row
  [3, 4, 5], // Middle row
  [6, 7, 8], // Bottom row
  [0, 3, 6], // Left column
  [1, 4, 7], // Middle column
  [2, 5, 8], // Right column
  [0, 4, 8], // Diagonal top-left to bottom-right
  [2, 4, 6], // Diagonal top-right to bottom-left
];

const checkWinner = (board) => {
  for (let combination of WINNING_COMBINATIONS) {
    const [a, b, c] = combination;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line: combination };
    }
  }

  // Check for draw
  if (board.every(square => square !== null)) {
    return { winner: 'draw', line: null };
  }

  return { winner: null, line: null };
};

const findBestMove = (board, aiSymbol, playerSymbol) => {
  // 1. Try to win
  for (let combination of WINNING_COMBINATIONS) {
    const [a, b, c] = combination;
    const line = [board[a], board[b], board[c]];

    // Check if AI can win
    if (line.filter(cell => cell === aiSymbol).length === 2 && line.filter(cell => cell === null).length === 1) {
      if (board[a] === null) return a;
      if (board[b] === null) return b;
      if (board[c] === null) return c;
    }
  }

  // 2. Block player from winning
  for (let combination of WINNING_COMBINATIONS) {
    const [a, b, c] = combination;
    const line = [board[a], board[b], board[c]];

    // Check if player can win and block them
    if (line.filter(cell => cell === playerSymbol).length === 2 && line.filter(cell => cell === null).length === 1) {
      if (board[a] === null) return a;
      if (board[b] === null) return b;
      if (board[c] === null) return c;
    }
  }

  // 3. Take center if available
  if (board[4] === null) {
    return 4;
  }

  // 4. Take corners
  const corners = [0, 2, 6, 8];
  const availableCorners = corners.filter(index => board[index] === null);
  if (availableCorners.length > 0) {
    return availableCorners[Math.floor(Math.random() * availableCorners.length)];
  }

  // 5. Take any available spot
  const availableSpots = board.map((spot, index) => spot === null ? index : null).filter(val => val !== null);
  return availableSpots[Math.floor(Math.random() * availableSpots.length)];
};

const findRandomMove = (board) => {
  const availableSpots = board.map((spot, index) => spot === null ? index : null).filter(val => val !== null);
  if (availableSpots.length === 0) return undefined;
  return availableSpots[Math.floor(Math.random() * availableSpots.length)];
};

export const useGameLogic = (difficulty, playerSymbol) => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [gameStatus, setGameStatus] = useState('setup'); // 'setup', 'playing', 'finished'
  const [winner, setWinner] = useState(null);
  const [winningLine, setWinningLine] = useState(null);

  const aiSymbol = playerSymbol === 'X' ? 'O' : 'X';

  const initializeGame = useCallback(() => {
    if (!playerSymbol) {
      return;
    }

    const newBoard = Array(9).fill(null);
    setBoard(newBoard);
    setGameStatus('playing');
    setWinner(null);
    setWinningLine(null);

    // Random chance for who goes first
    const playerGoesFirst = Math.random() < 0.5;
    if (playerGoesFirst) {
      setCurrentPlayer(playerSymbol);
    } else {
      setCurrentPlayer(aiSymbol);
    }
  }, [playerSymbol, aiSymbol]);

  const resetGame = useCallback(() => {
    if (!playerSymbol) {
      return;
    }

    const newBoard = Array(9).fill(null);
    setBoard(newBoard);
    setGameStatus('playing');
    setWinner(null);
    setWinningLine(null);

    // Random chance for who goes first on reset too
    const playerGoesFirst = Math.random() < 0.5;
    if (playerGoesFirst) {
      setCurrentPlayer(playerSymbol);
    } else {
      setCurrentPlayer(aiSymbol);
    }
  }, [playerSymbol, aiSymbol]);

  const makeMove = useCallback((index) => {
    if (board[index] !== null || gameStatus !== 'playing') {
      return;
    }

    const newBoard = [...board];
    newBoard[index] = currentPlayer;
    setBoard(newBoard);

    const result = checkWinner(newBoard);
    if (result.winner !== null) {
      setWinner(result.winner);
      setWinningLine(result.line);
      setGameStatus('finished');
      return;
    }

    // Switch to next player
    const nextPlayer = currentPlayer === playerSymbol ? aiSymbol : playerSymbol;
    setCurrentPlayer(nextPlayer);
  }, [board, currentPlayer, gameStatus, playerSymbol, aiSymbol]);

  // AI move logic
  useEffect(() => {
    if (currentPlayer === aiSymbol && gameStatus === 'playing' && playerSymbol && difficulty) {
      const timeoutId = setTimeout(() => {
        let aiMoveIndex;

        if (difficulty === 'Easy') {
          aiMoveIndex = findRandomMove(board);
        } else {
          aiMoveIndex = findBestMove(board, aiSymbol, playerSymbol);
        }

        if (aiMoveIndex !== undefined && aiMoveIndex !== null) {
          makeMove(aiMoveIndex);
        }
      }, 500); // Small delay for better UX

      return () => clearTimeout(timeoutId);
    }
  }, [currentPlayer, aiSymbol, board, difficulty, playerSymbol, makeMove, gameStatus]);

  return {
    board,
    currentPlayer,
    gameStatus,
    winner,
    winningLine,
    makeMove,
    resetGame,
    initializeGame
  };
};